import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { NoteDTO } from "@retrospecter/shared/types";
import { useRetro } from "../hooks/useRetro";
import { useAuth } from "../hooks/useAuth";
import { useSocket } from "../hooks/useSocket";
import { apiPatch, apiPost } from "../api/client";
import { ReadOnlyBanner } from "../components/ReadOnlyBanner";
import { PhaseControls } from "../components/Board/PhaseControls";
import { Timer } from "../components/Board/Timer";
import { Column, ComposeSpot } from "../components/Board/Column";
import { BoardAmbience } from "../components/Board/BoardAmbience";
import { TimerFlash } from "../components/Board/TimerFlash";
import { NotePile } from "../components/Board/NotePile";
import { ActionItems } from "../components/Board/ActionItems";
import { MoveDirection } from "../components/Board/NoteCard";
import {
  NUDGE_STEP,
  freeSpotInColumn,
  withPlacement,
} from "../components/Board/noteLayout";
import { useNoteDrag, NoteDrop } from "../hooks/useNoteDrag";
import { useTranslation } from "../i18n";

/** Where a note is being written: which canvas, and where on it. */
interface ComposeTarget extends ComposeSpot {
  columnId: string;
}

/** A note's box on its canvas. Everything a placement request needs. */
interface Placement {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
}

/** Lifecycle state → translation key. */
const STATUS_KEYS: Record<string, string> = {
  DRAFT: "board.statusDraft",
  ACTIVE: "board.statusActive",
  COMPLETED: "board.statusCompleted",
};

export function BoardPage() {
  const { publicId } = useParams<{ publicId: string }>();
  const navigate = useNavigate();
  const { retro, loading, refreshing, errorKind, refetch } = useRetro(publicId || "");
  const { auth, refresh: refreshAuth } = useAuth();
  const { t, plural, formatDate, columnTitle, templateName } = useTranslation();

  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState("");
  const [joinLoading, setJoinLoading] = useState(false);

  /** Local override so a moved note settles instantly, before the refetch. */
  const [pendingNotes, setPendingNotes] = useState<NoteDTO[] | null>(null);
  const [moveAnnouncement, setMoveAnnouncement] = useState("");
  const [timerFinished, setTimerFinished] = useState(false);
  /** Where the composer sits, and whether a blank note is being dragged to it. */
  const [composeAt, setComposeAt] = useState<ComposeTarget | null>(null);
  const [draggingNewNote, setDraggingNewNote] = useState(false);

  useSocket(publicId || "", refetch);

  const notes = pendingNotes ?? retro?.notes ?? [];

  // Grouped notes hang off their head note, so counting the top level alone
  // would quietly under-report the board every time someone grouped two cards.
  const noteCount = notes.reduce((total, note) => total + 1 + note.children.length, 0);

  // Once fresh server data arrives, drop the optimistic copy.
  useEffect(() => {
    setPendingNotes(null);
  }, [retro?.notes]);

  const handleJoin = async () => {
    if (!publicId) return;
    setJoinLoading(true);
    try {
      await apiPost(`/api/retrospectives/${publicId}/participants`, {});
      // Joining creates a participant session — reload it so the board knows
      // this viewer may now post, edit and vote.
      await refreshAuth();
      await refetch();
    } catch (err) {
      console.error("Join failed:", err);
    } finally {
      setJoinLoading(false);
    }
  };

  const handleActivateOrComplete = async () => {
    if (!publicId || !retro) return;

    setActionLoading(true);
    setActionError("");

    try {
      const endpoint =
        retro.status === "DRAFT"
          ? `/api/retrospectives/${publicId}/activate`
          : `/api/retrospectives/${publicId}/complete`;

      await apiPost(endpoint, {});
      await refetch();
    } catch (err) {
      setActionError(String(err));
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddNote = async (
    columnId: string,
    text: string,
    spot: ComposeSpot & { width: number; height: number }
  ) => {
    if (!publicId) return;
    try {
      await apiPost(`/api/retrospectives/${publicId}/notes`, {
        columnId,
        text,
        ...spot,
      });
      setComposeAt(null);
      await refetch();
    } catch (err) {
      console.error("Add note failed:", err);
    }
  };

  /**
   * Applies a placement locally, then persists it. The optimistic copy keeps a
   * post-it under the pointer where it was dropped instead of snapping back to
   * where it came from until the server answers.
   */
  const commitPlacement = useCallback(
    async (
      noteId: string,
      columnId: string,
      placement: Placement,
      { toFront = true }: { toFront?: boolean } = {}
    ) => {
      if (!notes.some((note) => note.id === noteId)) return;

      setPendingNotes(
        withPlacement(notes, noteId, { columnId, ...placement }, toFront)
      );

      try {
        await apiPatch(`/api/notes/${noteId}/move`, { columnId, ...placement });
      } catch (err) {
        console.error("Move failed:", err);
        setPendingNotes(null);
      } finally {
        await refetch();
      }
    },
    [notes, refetch]
  );

  /** Rests one note on another, or lifts it back out of its group. */
  const handleGroupNote = useCallback(
    async (noteId: string, parentId: string | null) => {
      try {
        await apiPatch(`/api/notes/${noteId}/group`, { parentId });
      } catch (err) {
        console.error("Group failed:", err);
      } finally {
        await refetch();
      }
    },
    [refetch]
  );

  const handleDropNote = useCallback(
    ({ noteId, columnId, x, y }: NoteDrop) => {
      commitPlacement(noteId, columnId, { x, y });
    },
    [commitPlacement]
  );

  const { drag, startDrag } = useNoteDrag({
    onDrop: handleDropNote,
    onGroup: handleGroupNote,
  });

  const handleResizeNote = (
    noteId: string,
    size: { width: number; height: number }
  ) => {
    const note = notes.find((item) => item.id === noteId);
    if (!note) return;
    if (note.width === size.width && note.height === size.height) return;

    // A resize leaves the stack alone: a note you made bigger should not also
    // jump in front of the notes it happens to overlap.
    commitPlacement(noteId, note.columnId, size, { toFront: false });
  };

  /**
   * Arrow-key equivalent of dragging, for people not using a pointer. Arrows
   * nudge the post-it around its canvas; with Shift they hand it to the column
   * either side, which is otherwise a pointer-only journey.
   */
  const handleKeyboardMove = (
    noteId: string,
    direction: MoveDirection,
    toColumn: boolean
  ) => {
    if (!retro) return;

    const note = notes.find((item) => item.id === noteId);
    if (!note) return;

    const columnIndex = retro.columns.findIndex((col) => col.id === note.columnId);

    if (toColumn && (direction === "left" || direction === "right")) {
      const targetColumn =
        retro.columns[columnIndex + (direction === "left" ? -1 : 1)];
      if (!targetColumn) return;

      // The note keeps its place on the canvas, so it arrives where the eye
      // expects rather than at the bottom of the new column.
      commitPlacement(noteId, targetColumn.id, { x: note.x, y: note.y });
      setMoveAnnouncement(
        t("note.movedToColumn", {
          column: columnTitle(targetColumn.title, retro.template.name),
        })
      );
      return;
    }

    const steps: Record<MoveDirection, { x: number; y: number }> = {
      up: { x: 0, y: -NUDGE_STEP },
      down: { x: 0, y: NUDGE_STEP },
      left: { x: -NUDGE_STEP, y: 0 },
      right: { x: NUDGE_STEP, y: 0 },
    };

    const step = steps[direction];
    const x = Math.max(0, note.x + step.x);
    const y = Math.max(0, note.y + step.y);
    // Already against that edge: nothing to move, and nothing to announce.
    if (x === note.x && y === note.y) return;

    commitPlacement(noteId, note.columnId, { x, y });
    setMoveAnnouncement(
      t("note.movedTo", {
        x: Math.round(x),
        y: Math.round(y),
        column: columnTitle(retro.columns[columnIndex].title, retro.template.name),
      })
    );
  };

  // SPEC §7: an unauthenticated request to a protected retro must send the user
  // to the login page with a returnTo pointing back here.
  useEffect(() => {
    if (errorKind === "unauthorized") {
      const currentPath = `/r/${publicId}`;
      navigate(`/login?returnTo=${encodeURIComponent(currentPath)}`, {
        replace: true,
      });
    }
  }, [errorKind, publicId, navigate]);

  const statusStyles: Record<string, string> = useMemo(
    () => ({
      DRAFT: "bg-amber-500/15 text-amber-600 dark:text-amber-300",
      ACTIVE: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-300",
      COMPLETED: "bg-ink/10 text-ink-muted",
    }),
    []
  );

  // Only take over the page when there is nothing to show yet. Refreshing an
  // on-screen board must not replace it with a spinner — that made every note
  // move, vote and socket update flash the whole screen.
  if (loading && !retro) {
    return (
      <div className="brand-wash min-h-screen">
        <div className="page-container" aria-busy="true">
          <span className="sr-only">{t("board.loading")}</span>

          {/* A skeleton of the board reads as the page arriving, rather than
              a spinner that says nothing about what is coming. */}
          <div className="mb-8 space-y-3">
            <div className="h-9 w-72 animate-pulse rounded-lg bg-surface-sunken" />
            <div className="h-4 w-52 animate-pulse rounded bg-surface-sunken" />
          </div>
          <div className="mb-8 grid gap-4 md:grid-cols-2">
            <div className="h-16 animate-pulse rounded-2xl bg-surface-sunken" />
            <div className="h-16 animate-pulse rounded-2xl bg-surface-sunken" />
          </div>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {[0, 1, 2].map((column) => (
              <div
                key={column}
                className="h-64 animate-pulse rounded-2xl bg-surface-sunken"
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Redirect is in flight; render nothing rather than flashing an error card.
  if (errorKind === "unauthorized") {
    return null;
  }

  if (errorKind === "forbidden" && !retro) {
    return (
      <div className="page-container flex min-h-[60vh] items-center justify-center">
        <div className="card max-w-md p-8 text-center">
          <div className="mb-4 text-4xl">🔒</div>
          <h1 className="mb-2 text-2xl font-bold text-ink">{t("board.accessDeniedTitle")}</h1>
          <p className="mb-6 text-ink-soft">
            {t("board.accessDeniedBody")}
          </p>
          <Link to="/" className="btn-primary inline-block">
            {t("common.returnHome")}
          </Link>
        </div>
      </div>
    );
  }

  if (!retro) {
    return (
      <div className="page-container flex min-h-[60vh] items-center justify-center">
        <div className="card max-w-md p-8 text-center">
          <div className="mb-4 text-4xl">❓</div>
          <h1 className="mb-2 text-2xl font-bold text-ink">{t("board.notFoundTitle")}</h1>
          <p className="mb-6 text-ink-soft">
            {t("board.notFoundBody")}
          </p>
          <Link to="/" className="btn-primary inline-block">
            {t("common.returnHome")}
          </Link>
        </div>
      </div>
    );
  }

  const isCompleted = retro.status === "COMPLETED";
  const isDraft = retro.status === "DRAFT";
  const isActive = retro.status === "ACTIVE";
  const isOwner = auth.staff?.username === retro.ownerStaffUsername;

  // Phase and timer are the facilitator's controls, and only while the board is
  // live. The server enforces this too (owner + ACTIVE); this just hides them.
  const canRunSession = isOwner && isActive;

  // A participant session is scoped to one board — holding one for a different
  // retro must not count as having joined this one.
  const hasJoined = auth.participant?.retrospectivePublicId === retro.publicId;
  const canContribute = isActive && hasJoined;

  // Templates range from three columns (Start/Stop/Continue) to five
  // (Starfish), so the grid tracks the actual column count.
  const gridColumns =
    {
      1: "md:grid-cols-1",
      2: "md:grid-cols-2",
      3: "md:grid-cols-2 xl:grid-cols-3",
      4: "md:grid-cols-2 xl:grid-cols-4",
      5: "md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5",
    }[retro.columns.length] ?? "md:grid-cols-2 xl:grid-cols-4";

  return (
    <div className="brand-wash relative min-h-screen">
      {/* Ambient motion only while the board is live: a draft is private setup
          and a completed board is an archive — neither wants to be animated. */}
      {isActive && <BoardAmbience />}

      {timerFinished && <TimerFlash onDone={() => setTimerFinished(false)} />}

      {isCompleted && <ReadOnlyBanner />}

      <div className="page-container relative z-10">
        <header className="mb-8 animate-fadeIn">
          <div className="mb-5 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div className="min-w-0">
              <div className="mb-2 flex flex-wrap items-center gap-3">
                <h1 className="text-3xl font-bold tracking-tight text-ink md:text-4xl">
                  {retro.name}
                </h1>
                <span className={`badge ${statusStyles[retro.status]}`}>
                  {t(STATUS_KEYS[retro.status] ?? "board.statusDraft")}
                </span>
              </div>

              <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-ink-muted">
                <span>{templateName(retro.template.name)}</span>
                <span aria-hidden="true">·</span>
                <span>{plural("list.participants", retro.participants.length)}</span>
                <span aria-hidden="true">·</span>
                <span>{plural("list.notes", noteCount)}</span>
                {retro.completedAt && (
                  <>
                    <span aria-hidden="true">·</span>
                    <span>
                      {t("list.completedOn", { date: formatDate(retro.completedAt) })}
                    </span>
                  </>
                )}
              </p>
            </div>

            {isOwner && !isCompleted && (
              <div className="flex flex-col items-stretch gap-2 md:items-end">
                {actionError && (
                  <div className="rounded-lg bg-red-500/10 p-3 text-sm text-red-500">
                    {actionError}
                  </div>
                )}
                <button
                  onClick={handleActivateOrComplete}
                  disabled={actionLoading}
                  data-testid={isDraft ? "activate-retro" : "complete-retro"}
                  className={
                    isDraft
                      ? "btn-accent whitespace-nowrap"
                      : "btn-primary whitespace-nowrap"
                  }
                >
                  {actionLoading
                    ? isDraft
                      ? t("board.activating")
                      : t("board.completing")
                    : isDraft
                    ? `🚀 ${t("board.activate")}`
                    : `✓ ${t("board.complete")}`}
                </button>
              </div>
            )}
          </div>

          {!isDraft && (
            <div className="grid gap-4 md:grid-cols-2">
              <PhaseControls
                publicId={publicId!}
                currentPhase={retro.currentPhase}
                canControl={canRunSession}
                onChanged={refetch}
              />
              <Timer
                publicId={publicId!}
                timerEndsAt={retro.timerEndsAt}
                canControl={canRunSession}
                onChanged={refetch}
                onFinished={() => setTimerFinished(true)}
              />
            </div>
          )}
        </header>

        {isActive && !hasJoined && (
          <div className="animate-slideUp mb-8 rounded-2xl border border-nm-primary/20 bg-gradient-to-r from-nm-primary/[0.07] to-nm-accent/[0.07] p-6 text-center">
            <p className="mb-4 font-medium text-ink-soft">
              {t("board.joinPrompt")}
            </p>
            <button
              onClick={handleJoin}
              disabled={joinLoading}
              data-testid="join-retro"
              className="btn-primary"
            >
              {joinLoading ? t("board.joining") : `👋 ${t("board.join")}`}
            </button>
          </div>
        )}

        {isDraft && (
          <div className="mb-8 rounded-2xl border border-amber-500/25 bg-amber-500/10 p-5 text-sm text-amber-700 dark:text-amber-300">
            {t("board.draftNotice")}
          </div>
        )}

        {canContribute && (
          <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
            <NotePile
              columns={retro.columns}
              templateName={retro.template.name}
              onDragStart={() => setDraggingNewNote(true)}
              onDragEnd={() => setDraggingNewNote(false)}
              onChooseColumn={(columnId) =>
                // No pointer, so no drop point: find the note a free spot on
                // that canvas rather than dropping it on top of another.
                setComposeAt({
                  columnId,
                  ...freeSpotInColumn(
                    columnId,
                    notes.filter((note) => note.columnId === columnId)
                  ),
                })
              }
            />
            <p className="text-xs text-ink-faint">{t("board.dragHint")}</p>
          </div>
        )}

        <div className={`grid gap-4 ${gridColumns}`} aria-busy={refreshing}>
          {retro.columns.map((column, columnIndex) => (
            <Column
              key={column.id}
              column={column}
              columnIndex={columnIndex}
              templateName={retro.template.name}
              // Back to front: a note's place in this list is what it overlaps.
              notes={notes
                .filter((note) => note.columnId === column.id)
                .sort((a, b) => a.position - b.position)}
              canContribute={canContribute}
              isDraggingNewNote={draggingNewNote}
              composeAt={composeAt?.columnId === column.id ? composeAt : null}
              onCompose={(columnId, spot) => setComposeAt({ columnId, ...spot })}
              onCancelCompose={() => setComposeAt(null)}
              canVote={canContribute}
              canMove={canContribute}
              isOwner={!!isOwner}
              isCompleted={isCompleted}
              drag={drag}
              onDragStartNote={startDrag}
              onResizeNote={handleResizeNote}
              onGroupNote={handleGroupNote}
              onKeyboardMove={handleKeyboardMove}
              onAddNote={handleAddNote}
              onChanged={refetch}
            />
          ))}
        </div>

        {/* Announces keyboard moves to screen readers. */}
        <p aria-live="polite" data-testid="move-announcement" className="sr-only">
          {moveAnnouncement}
        </p>

        {/* Hidden on a draft: there is nothing to act on until the team has
            actually met. A completed board still shows the record, read-only. */}
        {!isDraft && (
          <ActionItems
            publicId={publicId!}
            items={retro.actionItems}
            carried={retro.carriedActionItems}
            canEdit={canContribute || (!!isOwner && isActive)}
            onChanged={refetch}
          />
        )}

        <section
          aria-label={t("board.participantsRegion")}
          className="card mt-8 p-6"
        >
          <h3 className="mb-4 text-[0.7rem] font-bold uppercase tracking-[0.08em] text-ink-muted">
            {t("board.participantsHeading", { count: retro.participants.length })}
          </h3>

          {retro.participants.length === 0 ? (
            <p className="text-sm text-ink-faint">{t("board.noParticipants")}</p>
          ) : (
            <ul className="flex flex-wrap gap-2">
              {retro.participants.map((participant) => (
                <li
                  key={participant.id}
                  className="flex items-center gap-2 rounded-full border border-line bg-surface py-1 pl-1 pr-3"
                >
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-nm-primary/12 text-xs font-bold text-nm-primary">
                    {participant.displayName.charAt(0).toUpperCase()}
                  </span>
                  <span className="text-sm text-ink-soft">
                    {participant.displayName}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
